import { CurrencyCode } from '@/helpers/currency';
import { MediaType } from '@/helpers/enums';
import type {
  ILevelConfig,
  ILevelSegment,
} from '@/modules/promotions/templates/HeadlinePromoTemplate/components/MissionLevels/helpers';
import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';
import {
  createPromoImg,
  createPromoStage,
} from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers/promoImages';

const LVLS_IMAGE_PATH = '/headline-promo/halloween_haunt';

const getPromoImg = createPromoImg(LVLS_IMAGE_PATH);
const getStage = createPromoStage(LVLS_IMAGE_PATH);

const STAGE_1 = getStage(1);
const STAGE_2 = getStage(2);
const STAGE_3 = getStage(3);
const STAGE_4 = getStage(4);
const STAGE_5 = getStage(5);

type Segment = 'lv' | 'mv' | 'hv';

const DEFAULT_SEGMENT: Segment = 'lv';

const getRewardGroupKey = (segment: Segment, level: number) =>
  `halloween_haunt_${segment.toUpperCase()}_L${level}_reward`;

const createLevel = (
  level: number,
  name: string,
  stage: Pick<ILevelConfig, 'bg' | 'characterImage'>,
  segments: Record<Segment, Omit<ILevelSegment, 'rewardGroupKey'>>
): ILevelConfig => {
  const segmentsWithKeys = Object.fromEntries(
    (Object.keys(segments) as Segment[]).map((segment) => [
      segment,
      {
        ...segments[segment],
        groupId: `${segment}_total`,
        rewardGroupKey: getRewardGroupKey(segment, level),
      },
    ])
  ) as Record<Segment, ILevelSegment>;

  return {
    name,
    image: getPromoImg(`lvl_${level}`),
    lockedImage: getPromoImg(`lvl_${level}`),
    glowColor: '138, 56, 245',
    glowEndOpacity: 0.4,
    ...stage,
    promoRewardType: 'bets',
    ...segmentsWithKeys[DEFAULT_SEGMENT],
    segments: segmentsWithKeys,
  };
};

export const HALLOWEEN_CONFIG: IHeadlinePromoPageConfig = {
  promoKey: 'halloween',
  route: '/headline-promo',
  namespace: 'halloweenPromo26',
  disableAnimations: true,
  useFullNameAsGroupId: true,
  dateFormat: { default: 'MMM d', de: 'd. MMM', fr: 'd MMM' },
  hideHowToGamesCountLabel: false,
  progressBarVariant: 'pill',
  hideHowToDescription: false,
  faqBgImage: `${LVLS_IMAGE_PATH}/faq_decor.webp`,
  ctaAlwaysDeposit: true,
  currencyCode: CurrencyCode.EUR,
  hideCardProgressCurrency: true,
  widgetTitle: 'level',
  levelsMobileBreakpoint: MediaType.MOBILE_S,
  guestIcon: getPromoImg('lvl_5'),

  cardHud: {
    kind: 'image',
    src: `${LVLS_IMAGE_PATH}/hud_default.webp`,
  },

  levelsConfig: [
    createLevel(1, 'Lantern Walker', STAGE_1, {
      lv: {
        wagerAmounts: { 0: 0, 25: 5, 50: 10, 75: 15, 100: 20 },
        reward: { kind: 'freespins', count: 30 },
      },
      mv: {
        wagerAmounts: { 0: 0, 25: 38, 50: 75, 75: 113, 100: 150 },
        reward: { kind: 'freespins', count: 40 },
      },
      hv: {
        wagerAmounts: { 0: 0, 25: 250, 50: 500, 75: 750, 100: 1000 },
        reward: { kind: 'freespins', count: 50 },
      },
    }),
    createLevel(2, 'Bat Chaser', STAGE_2, {
      lv: {
        wagerAmounts: { 0: 20, 25: 28, 50: 35, 75: 43, 100: 50 },
        reward: { kind: 'freespins', count: 50 },
      },
      mv: {
        wagerAmounts: { 0: 150, 25: 213, 50: 275, 75: 338, 100: 400 },
        reward: { kind: 'freespins', count: 60 },
      },
      hv: {
        wagerAmounts: { 0: 1000, 25: 1375, 50: 1750, 75: 2125, 100: 2500 },
        reward: { kind: 'freespins', count: 100 },
      },
    }),
    createLevel(3, 'Potion Brewer', STAGE_3, {
      lv: {
        wagerAmounts: { 0: 50, 25: 63, 50: 75, 75: 88, 100: 100 },
        reward: { kind: 'freespins', count: 50 },
      },
      mv: {
        wagerAmounts: { 0: 400, 25: 500, 50: 600, 75: 700, 100: 800 },
        reward: { kind: 'freespins', count: 80, bonusAmount: 30 },
      },
      hv: {
        wagerAmounts: { 0: 2500, 25: 3125, 50: 3750, 75: 4375, 100: 5000 },
        reward: { kind: 'freespins', count: 150, bonusAmount: 100 },
      },
    }),
    createLevel(4, 'Ghost Hunter', STAGE_4, {
      lv: {
        wagerAmounts: { 0: 100, 25: 125, 50: 150, 75: 175, 100: 200 },
        reward: { kind: 'freespins', count: 70, bonusAmount: 10 },
      },
      mv: {
        wagerAmounts: { 0: 800, 25: 975, 50: 1150, 75: 1325, 100: 1500 },
        reward: { kind: 'freespins', count: 120, bonusAmount: 100 },
      },
      hv: {
        wagerAmounts: { 0: 5000, 25: 5750, 50: 6500, 75: 7250, 100: 8000 },
        reward: { kind: 'freespins', count: 200, bonusAmount: 250 },
      },
    }),
    createLevel(5, 'Midnight Legend', STAGE_5, {
      lv: {
        wagerAmounts: { 0: 200, 25: 275, 50: 350, 75: 425, 100: 500 },
        reward: {
          kind: 'bonus',
          amount: 90,
          labelKey: 'missionLevels.card.megaBonus',
          labelOnNewLine: true,
        },
      },
      mv: {
        wagerAmounts: { 0: 1500, 25: 1875, 50: 2250, 75: 2625, 100: 3000 },
        reward: {
          kind: 'bonus',
          amount: 370,
          labelKey: 'missionLevels.card.megaBonus',
          labelOnNewLine: true,
        },
      },
      hv: {
        wagerAmounts: {
          0: 8000,
          25: 9125,
          50: 10_250,
          75: 11_375,
          100: 12_500,
        },
        reward: {
          kind: 'bonus',
          amount: 1150,
          labelKey: 'missionLevels.card.megaBonus',
          labelOnNewLine: true,
        },
      },
    }),
  ],
};
